import type { ApolloError } from "@apollo/client";
import {
  type WorkspacePermissions,
  useWorkspaceStore
} from "stores/workspace/workspaceStore";

import type { Workspace } from "models/workspace";

/**
 * Payload shape accepted by the legacy dispatch shim.
 * Mirrors the union of all `Update*` types from the old `WorkspaceContextState`.
 */
interface WorkspaceDispatchPayload {
  workspaceId?: string;
  workspace?: Workspace;
  activeDashboardId?: string;
  activityBarWidth?: number;
  isActivityBarResizing?: boolean;
  isLoading?: boolean;
  error?: Maybe<ApolloError>;
  permissions?: WorkspacePermissions;
}

interface WorkspaceDispatchAction {
  type?: "update";
  payload: WorkspaceDispatchPayload;
}

type WorkspaceDispatchFn = (action: WorkspaceDispatchAction) => void;

/**
 * @deprecated Use `useWorkspaceStore` actions directly.
 *
 * This shim exists solely for backward compatibility during the Phase 6
 * migration. It translates the legacy `dispatch({ payload: { ... } })` calls
 * into individual Zustand store action calls.
 *
 * Removal deadline: end of Phase 7 cleanup.
 *
 * @example
 * // Before (legacy)
 * const dispatch = useWorkspaceDispatch();
 * dispatch({ payload: { workspaceId: "ws-1" } });
 *
 * // After (preferred)
 * import { useWorkspaceStore } from "stores/workspace/workspaceStore";
 * const setWorkspaceId = useWorkspaceStore((s) => s.setWorkspaceId);
 * setWorkspaceId("ws-1");
 */
export function useWorkspaceDispatch(): WorkspaceDispatchFn {
  const setWorkspaceId = useWorkspaceStore((s) => s.setWorkspaceId);
  const setWorkspace = useWorkspaceStore((s) => s.setWorkspace);
  const setActiveDashboardId = useWorkspaceStore((s) => s.setActiveDashboardId);
  const setActivityBarWidth = useWorkspaceStore((s) => s.setActivityBarWidth);
  const setActivityBarResizing = useWorkspaceStore((s) => s.setActivityBarResizing);
  const setLoading = useWorkspaceStore((s) => s.setLoading);
  const setError = useWorkspaceStore((s) => s.setError);
  const setPermissions = useWorkspaceStore((s) => s.setPermissions);

  return (action: WorkspaceDispatchAction) => {
    const { payload } = action;

    if (payload.workspaceId !== undefined) {
      setWorkspaceId(payload.workspaceId);
    }
    if (payload.workspace !== undefined) {
      setWorkspace(payload.workspace);
    }
    if (payload.activeDashboardId !== undefined) {
      setActiveDashboardId(payload.activeDashboardId);
    }
    if (payload.activityBarWidth !== undefined) {
      setActivityBarWidth(payload.activityBarWidth);
    }
    if (payload.isActivityBarResizing !== undefined) {
      setActivityBarResizing(payload.isActivityBarResizing);
    }
    if (payload.isLoading !== undefined) {
      setLoading(payload.isLoading);
    }
    if ("error" in payload) {
      setError(payload.error);
    }
    if (payload.permissions !== undefined) {
      setPermissions(payload.permissions);
    }
  };
}
